/**
 * AAAprinters - Profile Header Component
 * Displays user's avatar, name, and basic info
 */

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, Calendar, LogOut } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import type { User } from '@supabase/supabase-js';

interface ProfileHeaderProps {
  user: User;
  profile: any;
  memberSince?: string;
  onLogout: () => void;
}

export function ProfileHeader({ user, profile, memberSince, onLogout }: ProfileHeaderProps) {
  const { isRTL } = useLanguage();

  const userInitials = profile?.full_name
    ?.split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase() || user.email?.[0]?.toUpperCase() || "U";

  const formatMemberSince = (dateString?: string) => {
    if (!dateString) return "Recently joined";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="bg-gradient-to-r from-brand-cyan via-brand-magenta to-brand-yellow py-12 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col md:flex-row items-center gap-6">
          {/* Avatar */}
          <div className="relative">
            <Avatar className="h-32 w-32 border-4 border-white shadow-2xl ring-4 ring-white/20">
              <AvatarImage src={profile?.avatar_url} alt={profile?.full_name} />
              <AvatarFallback className="text-4xl font-bold bg-white text-primary">
                {userInitials}
              </AvatarFallback>
            </Avatar>
            <Badge className="absolute bottom-0 right-0 bg-green-500 text-white border-2 border-white shadow-lg">
              Active
            </Badge>
          </div>

          {/* User Info */}
          <div className="flex-1 text-center md:text-left">
            <h1 className="text-4xl font-bold text-white mb-2 drop-shadow-lg">
              {profile?.full_name || "Welcome!"}
            </h1>
            <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4 text-white/90">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <span className="text-sm">{user.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span className="text-sm">Member since {formatMemberSince(memberSince)}</span>
              </div>
            </div>
            {profile?.phone && (
              <p className="text-white/80 text-sm mt-2">
                {profile.phone}
              </p>
            )}
          </div>

          {/* Logout Button */}
          <Button
            variant="outline"
            className="bg-white hover:bg-white/90 border-0 shadow-lg"
            onClick={onLogout}
          >
            <LogOut className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
            Logout
          </Button>
        </div>
      </div>
    </div>
  );
}
